import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { eachDayOfInterval } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useTournamentPointer,
  useTournamentDetails,
  useNextAvailableTournament,
} from "@/lib/hooks/useApi";
import { NavigationMatch } from "@/components/NavigationMatch";
import type { Tournament } from "@/lib/types/api";
import { DatePickerCalendar } from "@/components/DatePickerCalendar";
import { DayTournamentView } from "@/components/DayTournamentView";
import AnimatedLoading from "@/components/AnimatedLoading";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";

dayjs.extend(utc);
dayjs.extend(timezone);

const getUtcEpochFromDate = (date: Date) => {
  const d = dayjs(date);
  return Math.floor(Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0) / 1000);
};

const getDateFromEpoch = (epoch?: number) => {
  if (!epoch || !Number.isFinite(epoch)) return undefined;
  return dayjs.unix(epoch).toDate();
};

function Tournament() {
  const navigate = useNavigate();
  const router = useRouter();
  const search = Route.useSearch();
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>();
  const [hasTournament, setHasTournament] = useState<boolean>(true);
  const [showPlayersList, setShowPlayersList] = useState<boolean>(false);

  // Get time-based assets based on tournament start time
  const getTimeBasedAssets = (startDate?: number, hasTournament?: boolean) => {
    // If no tournament, always use wall-border (not night version)
    if (!hasTournament) {
      return {
        arena: "https://cdn.navalabs.io/assets/arena-cropped_HhbSnf.png",
        wallBorderClass: "wall-border",
      };
    }

    if (!startDate) {
      // Default to current time if no start date
      const now = new Date();
      const hour = now.getHours();
      return {
        arena:
          hour >= 6 && hour < 18
            ? "https://cdn.navalabs.io/assets/arena-cropped_HhbSnf.png"
            : "https://cdn.navalabs.io/assets/arena-night_XDeccs.png",
        wallBorderClass:
          hour >= 6 && hour < 18 ? "wall-border" : "wall-border-night",
      };
    }

    // Use tournament start time
    const tournamentDate = new Date(startDate * 1000);
    const hour = tournamentDate.getHours();
    return {
      arena:
        hour >= 6 && hour < 18
          ? "https://cdn.navalabs.io/assets/arena-cropped_HhbSnf.png"
          : "https://cdn.navalabs.io/assets/arena-night_XDeccs.png",
      wallBorderClass:
        hour >= 6 && hour < 18 ? "wall-border" : "wall-border-night",
    };
  };

  const { data: pointer, isLoading: isPointerLoading } = useTournamentPointer();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: nextAvailableTournament, isLoading: isNextAvailableLoading } =
    useNextAvailableTournament();

  const { data: tournamentDetails, refetch: refetchTournamentDetails } =
    useTournamentDetails(selectedTournamentId ?? "");

  const { arena, wallBorderClass } = getTimeBasedAssets(
    Number(tournamentDetails?.startDate),
    hasTournament,
  );

  // // Refetch when tournament status changes
  // useEffect(() => {
  //   if (tournamentDetails?.status) {
  //     refetchTournamentDetails();
  //   }
  // }, [tournamentDetails?.status, refetchTournamentDetails]);

  const handleShowPlayersList = () => {
    setShowPlayersList(true);
  };

  const handleBackToMatch = () => {
    setShowPlayersList(false);
  };

  const setSearchDate = useCallback(
    (date: Date) => {
      const epoch = getUtcEpochFromDate(date);

      if (search.date === epoch) {
        return;
      }

      navigate({
        from: Route.fullPath,
        replace: true,
        search: (prev) => ({
          ...prev,
          date: epoch,
        }),
      });
    },
    [navigate, search.date],
  );

  const setSearchIndex = useCallback(
    (index: number) => {
      if (search.index === index) {
        return;
      }

      navigate({
        from: Route.fullPath,
        replace: true,
        search: (prev) => ({
          ...prev,
          index,
        }),
      });
    },
    [navigate, search.index],
  );

  const currentWeek = useMemo(() => {
    const date = getDateFromEpoch(search.date);
    return date ?? new Date();
  }, [search.date]);

  const weekDays = eachDayOfInterval({
    start: dayjs(currentWeek).subtract(3, "days").toDate(),
    end: dayjs(currentWeek).add(3, "days").toDate(),
  });

  const currentDayIndex = useMemo(() => {
    return weekDays.findIndex(
      (day) =>
        dayjs.unix(day.getTime() / 1000).format("YYYY-MM-DD") ===
        dayjs(currentWeek).format("YYYY-MM-DD"),
    );
  }, [weekDays, currentWeek]);

  const currentDay = weekDays[currentDayIndex] ?? currentWeek;

  useEffect(() => {
    const hasDate =
      typeof search.date === "number" && Number.isFinite(search.date);
    const hasIndex =
      typeof search.index === "number" && Number.isFinite(search.index);

    // If next available tournament is provided, set any missing params (date/index)
    if (nextAvailableTournament) {
      const updates: Partial<TournamentSearch> = {};

      const nextDateEpoch = Math.floor(
        Number(nextAvailableTournament.dateEpoch),
      );
      if (!hasDate && Number.isFinite(nextDateEpoch)) {
        updates.date = nextDateEpoch;
      }

      const nextIndex = Math.floor(Number(nextAvailableTournament.index));
      if (!hasIndex && Number.isFinite(nextIndex)) {
        updates.index = nextIndex;
      }

      if (Object.keys(updates).length > 0) {
        navigate({
          from: Route.fullPath,
          replace: true,
          search: (prev) => ({ ...prev, ...updates }),
        });
        return; // Avoid falling through to default date
      }
    }

    // Fallback: ensure date exists (index can remain undefined) using today
    if (!hasDate && !isPointerLoading) {
      setSearchDate(new Date());
    }
  }, [
    navigate,
    search.date,
    search.index,
    isPointerLoading,
    nextAvailableTournament,
    setSearchDate,
  ]);

  if (isPointerLoading || pointer === undefined) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <AnimatedLoading />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full relative overflow-hidden">
      <FloatingLeftButton
        onClick={() => router.navigate({ to: `/home` })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar
        //showFreePass
        className="absolute top-4 left-14 right-4 flex z-50 pl-3"
        rightExtra={
          tournamentDetails?.status !== "live" ? (
            <HowItWorkButton
              href="https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs/modes/tournament"
              size="size-8"
            />
          ) : undefined
        }
      />

      {/* Calendar Navigation - Top Right below ResourceBar */}
      <div className="absolute top-20 right-4 z-50 flex flex-col items-end gap-2">
        <DatePickerCalendar value={currentWeek} onChange={setSearchDate} />
      </div>

      <div
        className="rounded-lg flex-1 flex flex-col relative"
        //Remove background image from parents container
      >
        {/* Compute epoch as 00:00:00 UTC for the calendar day label */}
        {(() => {
          const d = dayjs(currentDay);
          const epoch = Math.floor(
            Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0) / 1000,
          );
          const canGoPreviousDay = currentDayIndex > 0;
          const canGoNextDay = currentDayIndex < weekDays.length - 1;
          const nextDay = canGoNextDay
            ? weekDays[currentDayIndex + 1]
            : undefined;
          const previousDay = canGoPreviousDay
            ? weekDays[currentDayIndex - 1]
            : undefined;

          return (
            <div
              className={`max-h-[60svh] ${wallBorderClass} flex flex-col flex-1 min-h-0`}
              style={{
                backgroundImage: hasTournament
                  ? `url(${arena})`
                  : "url(https://cdn.navalabs.io/assets/trellis_YpuLLp.png)",
                backgroundSize: hasTournament ? "100% 100%" : "100% 100%",
                backgroundPosition: "bottom",
              }}
            >
              <DayTournamentView
                epoch={epoch}
                inView={true}
                onTournamentSelect={setSelectedTournamentId}
                onHasTournamentChange={setHasTournament}
                tournamentDetails={tournamentDetails ?? ({} as Tournament)}
                canGoNextDay={canGoNextDay}
                canGoPreviousDay={canGoPreviousDay}
                onNextDay={
                  nextDay
                    ? () => {
                        setSearchDate(nextDay);
                      }
                    : undefined
                }
                onPreviousDay={
                  previousDay
                    ? () => {
                        setSearchDate(previousDay);
                      }
                    : undefined
                }
                onShowPlayersList={handleShowPlayersList}
                initialIndex={search.index}
                onIndexChange={setSearchIndex}
                onTournamentDetailsRefetch={refetchTournamentDetails}
              />
            </div>
          );
        })()}

        <NavigationMatch
          key={selectedTournamentId}
          tournamentId={selectedTournamentId ?? ""}
          tournamentDetails={tournamentDetails ?? ({} as Tournament)}
          hasTournament={hasTournament}
          entryAnalyticsMode="tournament"
          showPlayersList={showPlayersList}
          onBackToMatch={handleBackToMatch}
          refetchTournamentDetails={() => refetchTournamentDetails()}
          className="h-full max-h-[40svh]"
        />
      </div>

      <img
        src="https://cdn.navalabs.io/assets/varentino_jFPzPH.png"
        alt="Varentino"
        className="absolute bottom-[calc(40svh+12px)] left-2 w-16 h-16 object-contain z-50"
      />
    </div>
  );
}

type TournamentSearch = {
  date?: number;
  index?: number;
};

export const Route = createFileRoute("/(game)/tournament/")({
  validateSearch: (search): TournamentSearch => {
    const numericDate = Number(
      typeof search.date === "string" || typeof search.date === "number"
        ? search.date
        : undefined,
    );

    const numericIndex = Number(
      typeof search.index === "string" || typeof search.index === "number"
        ? search.index
        : undefined,
    );

    const result: TournamentSearch = {};

    if (Number.isFinite(numericDate) && numericDate > 0) {
      result.date = Math.floor(numericDate);
    }

    if (Number.isFinite(numericIndex) && numericIndex >= 0) {
      result.index = Math.floor(numericIndex);
    }

    return result;
  },
  component: Tournament,
});
